import React, {PureComponent} from "react";
import {DialogManagerProps} from "../../../../../store/dialog-manager/types";
import {connect, DispatchProp} from "react-redux";
import {IApplicationState} from "../../../../../store";
import {Overlay} from "../../../overlay/Overlay";
import dialogStyles from "../dialog.module.css";
import {cssHelper} from "../../../../utils/cssHelper";
import formStyles from "../../../../styles/forms.module.css";
import icons from "../../../../styles/icons.module.css";
import styles from "./credits-dialog.module.css";
import renderText from "../../../renderText";
import getFontSize from "../../../../utils/getFontSize";
import {iif} from "../../../iif";
import {sendReport} from "../../../../../store/common/actions";
import {IReport} from "../../../../../store/common/types";
import playSound from "../../../../sounds/playSound";
import i18n from "../../../../i18n";

interface ICreditsDialogProps {
    playerId?: string;
    playerName?: string;
    appVersion: string;
    zIndex?: number;
}

interface ICreditsDialogState {
    submitted: boolean;
    mode: 'credits' | 'report';
    report: IReport;
}

class CreditsDialog extends PureComponent<ICreditsDialogProps & DialogManagerProps & DispatchProp, ICreditsDialogState> {
    private player: React.RefObject<HTMLDivElement> = React.createRef();
    private form: React.RefObject<HTMLFormElement> = React.createRef();

    constructor(props: ICreditsDialogProps & DialogManagerProps & DispatchProp, context: any) {
        super(props, context);

        this.controlChanged = this.controlChanged.bind(this);

        this.state = {
            submitted: false,
            mode: 'credits',
            report: {
                name: props.playerName || '',
                email: '',
                text: ''
            }
        };
    }

    render() {
        const dialogBody = this.state.mode === 'credits'
            ? this.renderCredits()
            : this.renderReport();

        return (
            <Overlay zIndex={this.props.zIndex}>
                <button className={cssHelper(dialogStyles.back_button, icons.back).toString()} onClick={() => this.back()}>✖</button>

                {dialogBody}
            </Overlay>
        );
    }

    private renderCredits() {
        const {playerId, appVersion} = this.props;

        return (
            <div className={dialogStyles.container}>
                <div className={styles.info} style={{ fontSize: getFontSize(playerId?.length || 10, 24, 32, 12, 24) }} onClick={() => this.copyId()}>
                    <div ref={this.player}>
                        id: {playerId}
                    </div>
                    <div>v{appVersion}</div>
                </div>

                <figure className={`${dialogStyles.logo} logo`}/>

                {renderText(i18n("mainmenu.header"), 42, 60, 10, 20, styles.header)}
                {renderText(i18n("mainmenu.credits.developedby"), 28, 36, 24, 48, styles.text)}
                {renderText(i18n("mainmenu.credits.developer"), 36, 56, 14, 28, styles.text)}

                <div className={styles.actions} style={{opacity: this.state.submitted ? 0.7 : 1}}>
                    {iif(!this.state.submitted,
                        (
                            <button className={cssHelper(dialogStyles.button, dialogStyles.secondary_button).toString()} onClick={() => this.sendReport()}>
                                {i18n("mainmenu.credits.reportproblem")}
                            </button>
                        ),
                        renderText(i18n("mainmenu.credits.reportproblem.reported"), 28, 36, 24, 48, styles.text)
                    )}
                </div>
            </div>
        );
    }

    private renderReport() {
        const enterName = i18n("reportmenu.entername");
        const enterEmail = i18n("reportmenu.enteremail");
        const enterText = i18n("reportmenu.entertext");

        const fontSize = Math.min(
            getFontSize(enterName.length, 28, 36, 20, 36),
            getFontSize(enterEmail.length, 28, 36, 20, 36),
            getFontSize(enterText.length, 28, 36, 20, 36),
        );

        return (
            <div className={cssHelper(dialogStyles.container, styles.form_container).toString()}>
                <form ref={this.form} onSubmit={(event) => event.preventDefault()}>
                    <div className={formStyles.control_group}>
                        <label htmlFor="report_name" className={formStyles.control_label} style={{fontSize}}>{enterName}</label>
                        <input id="report_name" name={"name"} type="text" defaultValue={this.state.report.name} className={formStyles.control} onChange={this.controlChanged} required/>
                    </div>

                    <div className={formStyles.control_group}>
                        <label htmlFor="report_email" className={formStyles.control_label} style={{fontSize}}>{enterEmail}</label>
                        <input id="report_email" name={"email"} type="email" defaultValue={this.state.report.email} className={formStyles.control} onChange={this.controlChanged} required/>
                    </div>

                    <div className={formStyles.control_group}>
                        <label htmlFor="report_text" className={formStyles.control_label} style={{fontSize}}>{enterText}</label>
                        <textarea id="report_text" name={"text"} rows={6} defaultValue={this.state.report.text} className={formStyles.control} onChange={this.controlChanged} required/>
                    </div>

                </form>

                <div className={styles.actions}>
                    <button className={dialogStyles.button} onClick={() => this.submitReport()}>
                        {i18n("questionsubmit.submit")}
                    </button>
                    <button className={cssHelper(dialogStyles.button, styles.secondary_button).toString()} onClick={() => this.back()}>
                        {i18n("questionsubmit.cancel")}
                    </button>
                </div>
            </div>
        );
    }

    private controlChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        this.setState({
            report: {
                ...this.state.report,
                [event.target.name]: event.target.value
            }
        });
    }

    private submitReport() {
        playSound("button-click");

        if (this.form.current && this.form.current.checkValidity && !this.form.current.checkValidity())
            return;

        this.setState({
            mode: 'credits',
            submitted: true
        });

        this.props.dispatch(sendReport(this.state.report));
    }

    private sendReport() {
        playSound("button-click");
        this.setState({
            mode: 'report',
            report: {
                name: this.props.playerName || '',
                email: '',
                text: ''
            },
        });
    }

    private back() {
        playSound("button-click");

        if (this.state.mode === 'credits') {
            this.props.close();
        } else {
            this.setState({
                mode: 'credits'
            });
        }
    }

    private copyId() {
        const textArea = document.createElement("textarea") as HTMLTextAreaElement;
        textArea.style.position = "absolute";
        textArea.style.left = "-9999px";
        textArea.value = this.props.playerId!;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const copied = document.execCommand("copy");

            if (copied && this.player.current) {
                const prevText = this.player.current.innerHTML;
                this.player.current.innerHTML = i18n("mainmenu.credits.copied.id");
                this.player.current.style.opacity = "0.7";

                setTimeout(() => {
                    if (this.player.current) {
                        this.player.current.innerHTML = prevText;
                        this.player.current.style.opacity = "1";
                    }
                }, 750);
            }
        }
        catch (err) {
            console.error("Unable to copy player id");
        }
        finally {
            document.body.removeChild(textArea);
        }
    }
}

export default connect(({ player, settings }: IApplicationState) => ({
    playerId: (settings.userId || ("fb_" + player.id)) + "",
    playerName: player.name,
    appVersion: settings.appVersion
}))(CreditsDialog);